import { useCallback, useEffect, useRef, useState } from "react";
import PageShell from "../components/common/PageShell";
import { getResultsApi } from "../api/userApi";
import { dt, errMsg } from "../utils/format";
import { parseResultsResponse, sortLatestFirst } from "../utils/results";

const colorName = (color = "") =>
  color ? color.charAt(0).toUpperCase() + color.slice(1) : "-";

const keyOf = (r) => `${r.createdAt}-${r.winningNumber}`;

export default function Results() {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [raw, setRaw] = useState(null);
  const rowsRef = useRef([]);

  const load = useCallback(async (requestedPage = 1) => {
    setLoading(true);
    setErr("");
    try {
      const res = await getResultsApi({ page: requestedPage });
      const { list, pagination } = parseResultsResponse(res);
      console.log("[results] status:", res?.status, "rows:", list.length, res?.data);
      setRaw({ status: res?.status, data: res?.data });

      // Page 1 par replace, aage ke pages par append (duplicate hatake)
      const prev = requestedPage === 1 ? [] : rowsRef.current;
      const seen = new Set(prev.map(keyOf));
      const fresh = list.filter((r) => !seen.has(keyOf(r)));
      const merged = requestedPage === 1 ? list : sortLatestFirst([...prev, ...fresh]);
      rowsRef.current = merged;
      setRows(merged);

      if (pagination.page && Number(pagination.page) !== requestedPage) {
        console.warn(
          `[results] page ${requestedPage} maanga, server ne page ${pagination.page} diya - backend pagination check karo`
        );
      }

      // Server ke page number par bharosa nahi, apna requestedPage hi use karo
      const tp = Number(pagination.totalPages) || 1;
      const tot = Number(pagination.total) || merged.length;
      setPage(requestedPage);
      setTotal(tot);
      setTotalPages(tp);
      // naya data nahi aaya to aage mat badho (infinite "Load more" se bachav)
      setHasMore(
        requestedPage > 1 && fresh.length === 0
          ? false
          : pagination.hasMore !== undefined
          ? Boolean(pagination.hasMore) && requestedPage < tp
          : requestedPage < tp
      );
    } catch (e) {
      console.error("Results API error:", e);
      setErr(errMsg(e) || "Failed to load results.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(1);
  }, [load]);

  const last = rows[0];

  return (
    <PageShell title="Results">
      {err && (
        <div className="pg-lose">
          <p>{err}</p>
          <button type="button" className="pg-more" disabled={loading} onClick={() => load(page)}>
            Retry
          </button>
        </div>
      )}

      {loading && rows.length === 0 && <p className="pg-empty">Loading results...</p>}
      {!loading && !err && rows.length === 0 && (
        <>
          <p className="pg-empty">No spins found.</p>
          <pre style={{ color: "#ccc", fontSize: 11, whiteSpace: "pre-wrap", padding: 12 }}>
            {"API raw response: " + JSON.stringify(raw, null, 1)?.slice(0, 600)}
          </pre>
        </>
      )}

      {last && (
        <section className="pg-card pg-latest">
          <div className={`ball big ${last.winningColor || ""}`}>{last.winningNumber ?? "-"}</div>
          <div>
            <h2 style={{ margin: 0 }}>Last spin</h2>
            <span className="pg-muted">
              {colorName(last.winningColor)}, {dt(last.createdAt)}
            </span>
          </div>
        </section>
      )}

      {rows.length > 0 && (
        <section className="pg-card">
          <h2>All results</h2>
          <table className="pg-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Number</th>
                <th>Color</th>
                <th>Time</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={`${keyOf(r)}-${i}`}>
                  <td data-l="#">{i + 1}</td>
                  <td data-l="Number">
                    <div className={`ball sm ${r.winningColor || ""}`}>{r.winningNumber ?? "-"}</div>
                  </td>
                  <td data-l="Color">{colorName(r.winningColor)}</td>
                  <td data-l="Time">{dt(r.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="pg-legend pg-balls-note">
            <span><i style={{ background: "var(--red)" }} />Red</span>
            <span><i style={{ background: "#111", border: "1px solid var(--gold)" }} />Black</span>
            <span><i style={{ background: "#0a7d2c" }} />Green</span>
          </div>
        </section>
      )}

      {rows.length > 0 && (
        <p className="pg-muted pg-balls-note">
          Showing {rows.length} of {total} spins | Page {page} of {totalPages}
        </p>
      )}

      {hasMore && (
        <button type="button" className="pg-more" disabled={loading} onClick={() => load(page + 1)}>
          {loading ? "Loading..." : "Load more"}
        </button>
      )}
    </PageShell>
  );
}